/**
 * Accessories collection (sections/sunday-acc.liquid): one size for the page, prices that follow it,
 * Add in one tap. Words come from the page (section settings); sizes and prices from the variants.
 * The size is the same "bed" the rest of the site remembers (@sunday/state); a custom size takes the
 * standard size it is cut from. Add goes through @sunday/cart (a sized item joins the bed of the same
 * size in the cart; a pillow the first bed without one), then the card's Add turns into "− n in cart +" (@sunday/incart).
 */
import * as state from '@sunday/state';
import * as cart from '@sunday/cart';
import { paint as paintIn } from '@sunday/incart';
import { rupees } from '@sunday/sizes';
import { esc } from '@sunday/util';

class SundayAcc extends HTMLElement {
  connectedCallback() {
    if (this.words) return;
    this.words = JSON.parse(this.querySelector('script[data-acc-words]').textContent);
    this.select = this.querySelector('[data-size]');
    this.$err = this.querySelector('[data-err]');
    this.cards = [...this.querySelectorAll('.sd-acc2')].map((el) => ({ el, cfg: JSON.parse(el.querySelector('script[type="application/json"]').textContent) }));
    this.sizes = [...this.select.options].map((o) => o.value);

    const bed = state.get('bed');
    const fromCart = this.hasAttribute('data-from-cart'); // a mattress in the cart decides the size
    if (bed && bed.w && bed.l && !fromCart) {
      const pick = this.cutFrom(bed.w, bed.l);
      if (pick) this.select.value = pick;
      this.showCustom(bed, pick);
    }
    this.sizeOut();
    this.select.addEventListener('change', () => {
      const [w, l] = this.select.value.split('x').map(Number);
      state.set('bed', { w, l, from: 'accessories' });
      this.sizeOut();
      this.showCustom(null);
      this.paint();
      this.dock(this.cart);
    });
    this.addEventListener('click', (e) => {
      const pv = e.target.closest('.sd-acpv button[data-variant]');
      if (pv) {
        for (const b of pv.parentElement.querySelectorAll('button')) b.setAttribute('aria-pressed', String(b === pv));
        const card = pv.closest('.sd-acc2');
        const ab = card.querySelector('[data-add]');
        ab.dataset.variant = pv.dataset.variant;
        ab.dataset.sdKind = 'a';
        ab.dataset.sdV = pv.dataset.variant;
        paintIn(ab);
        card.querySelector('[data-price-out]').textContent = rupees(Number(pv.dataset.price));
        return;
      }
      const add = e.target.closest('[data-add]');
      if (add) this.add(add);
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-acc-change]')) return;
      this.select.scrollIntoView({ block: 'center', behavior: 'smooth' });
      this.select.focus({ preventScroll: true });
    });
    document.addEventListener('sunday:cart', (e) => {
      this.cart = e.detail.cart;
      this.dock(this.cart);
    });
    this.paint();
    cart.get().then((c) => { this.cart = c; this.dock(c); }).catch(() => this.dock(null));
  }

  /** The quiet size line shows the size picked. */
  sizeOut() {
    const o = this.select.selectedOptions[0];
    const b = this.querySelector('[data-size-out]');
    if (b && o) b.textContent = o.textContent;
  }

  /** The smallest listed size that holds a w × l mattress (a custom size is cut from it). */
  cutFrom(w, l) {
    const fits = this.sizes.map((k) => k.split('x').map(Number)).filter(([sw, sl]) => sw >= w && sl >= l);
    fits.sort((a, b) => a[0] * a[1] - b[0] * b[1]);
    return fits.length ? `${fits[0][0]}x${fits[0][1]}` : null;
  }

  showCustom(bed, pick) {
    const p = this.querySelector('[data-custom]');
    if (!p) return;
    if (!bed || !bed.custom || !pick) {
      p.hidden = true;
      return;
    }
    const label = this.select.selectedOptions[0] ? this.select.selectedOptions[0].textContent : pick;
    p.textContent = p.dataset.text.replace('{custom}', `${bed.l}×${bed.w}″`).replace('{size}', label);
    p.hidden = false;
  }

  paint() {
    const [w, l] = this.select.value.split('x').map(Number);
    for (const { el, cfg } of this.cards) {
      const btn = el.querySelector('[data-add]');
      if (el.dataset.sized !== 'true') {
        btn.dataset.sdKind = 'a';
        btn.dataset.sdV = btn.dataset.variant;
        paintIn(btn);
        continue;
      }
      const v = cfg.variants.find((x) => x[1] === w && x[2] === l);
      if (!v) {
        btn.disabled = true;
        continue;
      }
      btn.disabled = false;
      btn.dataset.variant = v[0];
      btn.dataset.sdKind = 'a';
      btn.dataset.sdV = v[0];
      paintIn(btn);
      el.querySelector('[data-price-out]').textContent = v[4];
      el.querySelectorAll('[data-unit]').forEach((u) => { u.textContent = v[5]; });
    }
  }

  async add(btn) {
    const card = btn.closest('.sd-acc2');
    const { cfg } = this.cards.find((c) => c.el === card);
    const id = Number(btn.dataset.variant);
    const v = cfg.variants.find((x) => x[0] === id);
    const sized = card.dataset.sized === 'true';
    if (btn.getAttribute('aria-busy') === 'true') return;
    this.$err.hidden = true;
    // The tap answers at once (sunday-sheets.js): "Adding…" on the button, then "− n in cart +".
    const item = { btn, name: cfg.name, what: card.querySelector('[data-unit]').textContent, price: v[4], image: cfg.image, tint: 'var(--sd-ground)' };
    document.dispatchEvent(new CustomEvent('sunday:adding', { detail: item }));
    try {
      const res = await cart.addFromProductPage({ variantId: id, productId: Number(card.dataset.product), size: sized ? { w: v[1], l: v[2] } : null, anyBed: !sized });
      const line = res.cart.items.find((x) => x.variant_id === id) || res.cart.items[0];
      this.cart = res.cart;
      document.dispatchEvent(
        new CustomEvent('sunday:added', {
          detail: { ...item, cart: res.cart, line, stay: true },
        })
      );
      this.dock(res.cart);
    } catch (e) {
      this.$err.textContent = e.message || '';
      this.$err.hidden = false;
      document.dispatchEvent(new CustomEvent('sunday:add-failed', { detail: { btn, message: e.message || '' } }));
    }
  }

  /** Phones: before anything is added, the size and a navy Change; after, the total and Go to cart. */
  dock(c) {
    const d = document.getElementById('dock');
    if (!d) return;
    const menu = d.querySelector('.sd-dmenu');
    for (const n of [...d.children]) if (n !== menu) n.remove();
    const n = c ? c.item_count : 0;
    if (n > 0) {
      d.insertAdjacentHTML('beforeend', `<div class="sd-bp"><b>${esc(rupees(c.total_price))}</b><span>${esc(this.words.nInCart.replace('{n}', n))}</span></div><a class="sd-btn sd-ago" data-dock-main href="${esc(window.Sunday?.cartUrl || '/cart')}">${esc(this.words.go)}</a>`);
    } else {
      const label = this.select.selectedOptions[0] ? this.select.selectedOptions[0].textContent : '';
      d.insertAdjacentHTML('beforeend', `<div class="sd-bp"><b>${esc(label)}</b><span>${esc(this.words.pricesFor)}</span></div><button type="button" class="sd-btn sd-nv" data-acc-change>${esc(this.words.change)}</button>`);
    }
  }
}

if (!customElements.get('sunday-acc')) customElements.define('sunday-acc', SundayAcc);
